import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2, ThumbsUp, ThumbsDown, Lightbulb, ListChecks, Activity, Stethoscope } from "lucide-react";

function Column({ icon: Icon, title, color, items }) {
  return (
    <div className="glass rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-5 h-5" style={{ color }} />
        <h3 className="font-display font-semibold">{title}</h3>
      </div>
      <ul className="space-y-2">
        {(items || []).map((it, i) => (
          <li key={`${it}-${i}`} className="text-sm text-slate-300 flex gap-2">
            <span style={{ color }}>•</span> {it}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function DiagnosisView({ brand }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = async () => {
    if (!brand) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await api.get(`/brands/${brand.brand_id}/diagnosis`);
      setReport(res.data[0] || null);
    } catch (e) { console.error("Erro ao carregar diagnóstico:", e); } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [brand?.brand_id]);

  const run = async () => {
    if (!brand) return;
    setRunning(true);
    try {
      const res = await api.post(`/brands/${brand.brand_id}/diagnosis`);
      setReport(res.data);
      toast.success("Diagnóstico concluído pelo Analista!");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao gerar diagnóstico."); }
    finally { setRunning(false); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="overline text-cyan-400 mb-1">Etapa 2 — Diagnóstico</p>
          <h2 className="font-display text-2xl font-bold tracking-tight">Analista de Conteúdo</h2>
          <p className="text-sm text-slate-400 mt-1">DADOS → DIAGNÓSTICO → OPORTUNIDADE → AÇÃO</p>
        </div>
        <Button data-testid="btn-run-diagnosis" onClick={run} disabled={running || !brand}
          className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold hover:brightness-110 transition-all">
          {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Stethoscope className="w-4 h-4 mr-2" />}
          Gerar diagnóstico
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>
      ) : !report ? (
        <div className="glass rounded-2xl p-10 text-center text-slate-400">
          <Activity className="w-10 h-10 mx-auto mb-3 text-slate-600" />
          Nenhum diagnóstico ainda. Clique em "Gerar diagnóstico".
        </div>
      ) : (
        <div className="space-y-5">
          <div className="glass rounded-2xl p-6" data-testid="diagnosis-summary">
            <div className="flex items-center gap-4 mb-3">
              <div className="relative w-16 h-16 shrink-0">
                <svg className="w-16 h-16 -rotate-90"><circle cx="32" cy="32" r="28" stroke="#1e293b" strokeWidth="6" fill="none" />
                  <circle cx="32" cy="32" r="28" stroke="#06B6D4" strokeWidth="6" fill="none"
                    strokeDasharray={`${2 * Math.PI * 28}`} strokeDashoffset={`${2 * Math.PI * 28 * (1 - (report.score || 0) / 100)}`} strokeLinecap="round" />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center font-mono font-bold text-cyan-300 tabular-nums">{report.score || 0}</span>
              </div>
              <div>
                <p className="overline text-cyan-400">Saúde do posicionamento</p>
                <p className="text-sm text-slate-300 leading-relaxed mt-1">{report.resumo}</p>
              </div>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Column icon={ThumbsUp} title="Pontos Fortes" color="#10B981" items={report.pontos_fortes} />
            <Column icon={ThumbsDown} title="Pontos Fracos" color="#EF4444" items={report.pontos_fracos} />
            <Column icon={Lightbulb} title="Oportunidades" color="#F59E0B" items={report.oportunidades} />
            <Column icon={ListChecks} title="Próximas Ações" color="#8B5CF6" items={report.proximas_acoes} />
          </div>
        </div>
      )}
    </div>
  );
}
